export default function PhilosophySection() {
  const cards = [
    {
      type: "quote",
      text: "Every system is designed with clean separation of concerns, ensuring that architecture decisions support long-term maintainability and adaptability as requirements evolve over time.",
    },
    {
      type: "quote",
      text: "Performance is treated as a first-class design constraint, not an afterthought. Every component is profiled, optimized, and validated against real-world usage patterns.",
    },
    {
      type: "dark",
      heading: "User-Centered",
      sub: "Design First",
    },
    {
      type: "dark-wide",
      heading: "Continuous Innovation",
      sub: "Engineering Standards",
      detail: "Active exploration of new technologies and continuous evolution of internal processes maintain high technical excellence across every project phase.",
    },
    {
      type: "quote-small",
      text: "Reliability is engineered in from the start through rigorous testing, thoughtful error handling, and defensive design patterns.",
      label: "Stability",
    },
    {
      type: "quote-small",
      text: "Development workflows are continuously refined to eliminate bottlenecks and improve team velocity without compromising quality.",
      label: "Workflow",
    },
  ];

  return (
    <section className="relative w-full bg-secondary-50 py-28 md:py-36 px-10 md:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-primary-500 rounded-sm rotate-45"></span>
            <span className="text-xs text-primary-600 font-label tracking-[0.15em] uppercase">
              Engineering Philosophy
            </span>
          </div>
          <h3 className="text-4xl md:text-[44px] font-heading font-bold text-primary-700">
            Principles &amp; Standards
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, index) => {
            if (card.type === "quote") {
              return (
                <div
                  key={index}
                  className="bg-secondary-100/80 rounded-[20px] p-10 flex flex-col"
                >
                  <div className="text-6xl text-primary-300/40 font-serif leading-none mb-6">
                    &ldquo;
                  </div>
                  <p className="text-[17px] text-primary-800 leading-[1.6] font-body">
                    {card.text}
                  </p>
                </div>
              );
            }

            if (card.type === "dark") {
              return (
                <div
                  key={index}
                  className="relative rounded-[20px] overflow-hidden min-h-[280px]"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20dark%20warm%20teal%20green%20geometric%20texture%20smooth%20gradients%20minimal%20modern%20elegant%20no%20people%20no%20text%20no%20interfaces%20atmospheric%20depth&width=800&height=600&seq=phil-card-dark-01&orientation=landscape"
                    alt="Abstract geometric texture for engineering philosophy"
                    title="Engineering Philosophy - User-Centered Design"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-primary-900/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-8">
                    <p className="text-base font-semibold text-white font-heading">
                      {card.heading}
                    </p>
                    <p className="text-sm text-white/80 font-body mt-1">
                      {card.sub}
                    </p>
                  </div>
                </div>
              );
            }

            if (card.type === "dark-wide") {
              return (
                <div
                  key={index}
                  className="relative rounded-[20px] overflow-hidden min-h-[280px] md:col-span-2"
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20dark%20warm%20olive%20green%20geometric%20layered%20texture%20smooth%20flowing%20gradients%20minimal%20modern%20architectural%20composition%20no%20people%20no%20text%20no%20interfaces&width=1200&height=600&seq=phil-card-wide-01&orientation=landscape"
                    alt="Abstract layered geometric texture for continuous innovation"
                    title="Engineering Philosophy - Continuous Innovation"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/85 via-primary-900/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-8 max-w-md">
                    <p className="text-xl font-semibold text-white font-heading">
                      {card.heading}
                    </p>
                    <p className="text-sm text-white/80 font-body mt-2">
                      {card.sub}
                    </p>
                    <p className="text-[13px] text-white/70 font-body leading-relaxed mt-3">
                      {card.detail}
                    </p>
                  </div>
                </div>
              );
            }

            if (card.type === "quote-small") {
              return (
                <div
                  key={index}
                  className="bg-secondary-100/80 rounded-[20px] p-8 flex flex-col justify-between"
                >
                  <div>
                    <div className="text-5xl text-primary-300/40 font-serif leading-none mb-4">
                      &ldquo;
                    </div>
                    <p className="text-sm text-primary-800 leading-[1.6] font-body">
                      {card.text}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-6 pt-4 border-t border-secondary-200/60">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-[11px] text-primary-600 font-label tracking-wider uppercase">
                      {card.label}
                    </span>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </section>
  );
}